<template>
  <!-- desktop version -->
  <transition-group
    tag="div"
    name="gallery-items"
    class="horizontal-gallery"
    :style="{height: `${viewPort.height * 0.6}px`}"
    v-if="$vuetify.breakpoint.mdAndUp"
    >
    <ClipMask key="clip-mask" :label="title ? title : (getSelectedPhoto(centralIndex) ? getSelectedPhoto(centralIndex).artist.name : null)" :masked="false" />
    <div
      v-for="(element, i) in currentSlice"
      :key="element.id"
      class="gallery-element"
      :style="imageStyle(i, currentSlice)"
      @click.capture="onImageClick(i, $event)"
      :data-leavex="i > centralIndex ? '100%' : i < centralIndex ? '-100%' : 0"
      @wheel="onMouseWheel"
      @touchmove.prevent
      v-touch="{left: goToNextImage, right: goToPreviousImage}"
    >
      <transition mode="out-in">
        <v-row v-if="getSelectedPhoto(i)" :key="getSelectedPhoto(i).details.photo.title" no-gutters class="artist-info">
          <v-col cols="8" offset="2">
            <v-row align="center" justify="center" no-gutters >
              <v-col cols="12" v-if="getSelectedPhoto(i).details.series && !seriesless">
                <v-btn text block class="text-decoration-underline font-weight-light grey--text" :to="{name: 'Artist', hash: `#${slugify(getSelectedPhoto(i).details.series.replace(/%([0-9])%/gm, '').toLowerCase())}`,  params: {slug: getSelectedPhoto(i).artist._slug}}">
                  {{getSelectedPhoto(i).details.series.replace(/%([0-9])%/gm, '')}}
                </v-btn>
              </v-col>
              <v-col cols="12" v-if="getSelectedPhoto(i).artist">
                <v-btn text block class="text-decoration-underline font-weight-regular" :to="{name: 'Artist', params: {slug: getSelectedPhoto(i).artist._slug}}">
                  {{getSelectedPhoto(i).artist.name}}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </transition>
      <PhotoExhibitionElement
        :data="element"
        class="fill-height no-hover-fade"
        :position="i < centralIndex ? 'right' : i > centralIndex ? 'left' : 'center'"
        :data-cursor-target="i < centralIndex ? 'previous' : i > centralIndex ? 'previous||1|180' : 'default'"
        @click="setSelectedPhoto(i, $event)"
        />
      <transition mode="out-in">
        <v-row v-if="getSelectedPhoto(i)" :key="getSelectedPhoto(i).details.photo.title" no-gutters class="photo-info">
          <v-col cols="12">
            <span class="d-block text-center body-1">{{getSelectedPhoto(i).details.photo.title}}</span>
          </v-col>
        </v-row>
      </transition>
    </div>
  </transition-group>
  <!-- mobile version -->
  <v-row v-else no-gutters class="vertical-gallery pt-14 pt-md-0">
    <v-col
      cols="12"
      v-for="(element, i) in elements"
      :key="element.id"
      :id="`vertical-element-${i}`"
      ref="verticalElement"
    >
      <v-responsive :aspect-ratio="getMobileAspectRatio(element)" v-intersect="{options: {threshold: 0.8}, handler: (entries, observer, isIntersecting) => onIntersect(element, i.toString(), isIntersecting)}">
        <PhotoExhibitionElement
          :data="element"
          class="fill-height"
          position="center"
          @click="$set(selectedMobilePhoto, i, $event)"
          />
      </v-responsive>
      <div class="px-8 mb-16 mt-2" v-if="getSelectedMobilePhoto(i, element)">
        <router-link
          v-if="getSelectedMobilePhoto(i, element).details.series"
          class="text-decoration-underline font-weight-light grey--text text-left px-0"
          :to="{name: 'Artist', hash: `#${slugify(getSelectedMobilePhoto(i, element).details.series.replace(/%([0-9])%/gm, '').toLowerCase())}`,  params: {slug: getSelectedMobilePhoto(i, element).artist._slug}}"
        >
          {{getSelectedMobilePhoto(i, element).details.series.replace(/%([0-9])%/gm, '')}}
        </router-link>
        <br />
        <router-link
          v-if="getSelectedMobilePhoto(i, element).artist"
          class="text-decoration-underline font-weight-regular text-left px-0"
          :to="{name: 'Artist', params: {slug: getSelectedMobilePhoto(i, element).artist._slug}}"
        >
          {{getSelectedMobilePhoto(i, element).artist.name}}
        </router-link>
        <span class="d-block body-1">{{getSelectedMobilePhoto(i, element).details.photo.title}}</span>
        <span class="d-block body-1">-</span>
      </div>
      <v-responsive v-else :aspect-ratio="1.8" />
    </v-col>
  </v-row>
</template>

<script>

import findIndex from 'lodash/findIndex'
import isNil from 'lodash/isNil'
import find from 'lodash/find'
import throttle from 'lodash/throttle'
import { Viewport } from '@monogrid/vue-lib'
import PhotoExhibitionElement from '@/atoms/PhotoExhibitionElement'
import ClipMask from '@/molecules/ClipMask'
import slugify from 'slugify'
import delay from 'lodash/delay'
import { getAspectRatio } from '@/services/OptimizedImage'
import { normalizeWheel } from '@monogrid/js-utils'

export default {
  name: 'GalleryHorizontalView',
  mixins: [Viewport],
  components: {
    PhotoExhibitionElement,
    ClipMask
  },
  props: {
    elements: {
      type: Array,
      required: true
    },
    artist: {
      type: String,
      required: true
    },
    index: {
      type: String,
      required: false
    },
    routeless: {
      type: Boolean,
      required: false,
      default: false
    },
    seriesless: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      currentElementIndex: -1,
      elementsSelectedPhoto: [], // a 1:1 mapping of elements containing only the selected photo (0 for single photos, [null until selected] for groups)
      selectedMobilePhoto: []
    }
  },
  mounted () {
    this.calculateSlugGalleryIndex()
    this.elementsSelectedPhoto = this.elements.map(g => g.length > 1 ? null : g.photos[0])

    this.scrollToVerticalElement()
  },
  watch: {
    '$vuetify.breakpoint.name' () {
      this.scrollToVerticalElement()
      this.calculateSlugGalleryIndex()
    },
    currentElementIndex (value) {
      if (this.elements[this.currentElementIndex].photos.length > 1) {
        this.elementsSelectedPhoto[this.currentElementIndex] = null
      }
    },
    elements () {
      this.calculateSlugGalleryIndex()
      this.elementsSelectedPhoto = this.elements.map(g => g.length > 1 ? null : g.photos[0])
    }
  },
  computed: {
    numElements () {
      if (this.elements.length > 5) {
        return 5
      }
      return 3
    },
    centralIndex () {
      if (this.elements.length > 5) {
        return 2
      }
      return 1
    },
    currentSlice () {
      if (this.elements && this.currentElementIndex !== -1) {
        const start = this.absmod(this.currentElementIndex - this.centralIndex, this.elements.length)
        const ret = []
        for (let i = start; i < start + this.numElements; i++) {
          ret.push(this.elements[this.absmod(i, this.elements.length)])
        }
        return ret
      }
      return []
    }
  },
  methods: {
    onIntersect (element, index, isIntersecting) {
      if (isIntersecting) {
        console.log('intersect', element)
        if (!this.routeless) {
          this.$router.replace({ params: { index, artist: element.photos[0].artist._slug } })
        }
      }
    },
    slugify (string) {
      return encodeURIComponent(slugify(string).toLowerCase().replace(/^[^a-z]+|[^\w:.-]+/gi, '')).replace(/%[0-9A-F]{2}/gi, '')
    },
    getAspectRatio,
    onMouseWheel: throttle(function (e) {
      console.log('mouse wheel')
      const evt = normalizeWheel(e)
      if (evt.pixelX !== 0) e.preventDefault()
      const scroll = evt.pixelY + evt.pixelX
      if (scroll > 0) {
        this.goToNextImage()
      } else {
        this.goToPreviousImage()
      }
    }, 600, { trailing: false }),
    getMobileAspectRatio (element) {
      if (element.photos.length === 1 && element.photos[0]) {
        if (element.photos[0].details.photo.image.mimeType.indexOf('video/') !== -1) {
          // A video
          return 16 / 9
        }
        return getAspectRatio(element.photos[0].details.photo.image)
      }
      return 0.7 // more than one photo, this works
    },
    scrollToVerticalElement () {
      if (this.$vuetify.breakpoint.mdAndUp || this.routeless) return

      if (this.$refs.verticalElement) {
        delay(() => this.$vuetify.goTo(`#vertical-element-${this.currentElementIndex}`), 500)
      } else {
        delay(this.scrollToVerticalElement, 10)
      }
    },
    getSelectedMobilePhoto (i, element) {
      if (element.photos.length === 1) return element.photos[0]
      return this.selectedMobilePhoto[i]
    },
    getSelectedPhoto (sliceIndex) {
      if (!this.currentSlice[sliceIndex]) return null
      const totalArrayIndex = findIndex(this.elements, { id: this.currentSlice[sliceIndex].id })
      return this.elementsSelectedPhoto[totalArrayIndex]
    },
    setSelectedPhoto (sliceIndex, photo) {
      const totalArrayIndex = findIndex(this.elements, { id: this.currentSlice[sliceIndex].id })
      this.$set(this.elementsSelectedPhoto, totalArrayIndex, photo)
    },
    onImageClick (i, e) {
      if (i > this.centralIndex) {
        this.goToNextImage()
        e.stopPropagation()
      } else if (i < this.centralIndex) {
        this.goToPreviousImage()
        e.stopPropagation()
      }
    },
    goToNextImage () {
      this.currentElementIndex = this.absmod(this.currentElementIndex + 1, this.elements.length)
      if (!this.routeless) {
        this.$router.replace({ params: { index: this.currentElementIndex.toString(), artist: this.elements[this.currentElementIndex].photos[0].artist._slug } })
      }
    },
    goToPreviousImage () {
      this.currentElementIndex = this.absmod(this.currentElementIndex - 1, this.elements.length)
      if (!this.routeless) {
        this.$router.replace({ params: { index: this.currentElementIndex.toString(), artist: this.elements[this.currentElementIndex].photos[0].artist._slug } })
      }
    },
    imageStyle (index, key) {
      const spacing = 40
      const w = (this.viewPort.width * 0.9) - (spacing * 2)
      const x = (w + (spacing / 2)) * (index - this.centralIndex) + ((this.viewPort.width - w) / 2)
      return {
        cursor: index === this.centralIndex ? null : 'pointer',
        width: `${w}px`,
        left: `${x}px`,
        '--leavex': index > this.centralIndex ? '100%' : index < this.centralIndex ? '-100%' : 0
      }
    },
    calculateSlugGalleryIndex () {
      // if (this.$vuetify.breakpoint.smAndDown || this.routeless) return

      if (this.elements) {
        // console.log('ok')
        if (isNil(this.index)) {
          // return first index
          this.currentElementIndex = findIndex(this.elements, (elem) => {
            return !!find(elem.photos, { artist: { _slug: this.artist } })
          })
        } else {
          this.currentElementIndex = parseFloat(this.index)
        }
      }
    },
    absmod (n, m) {
      return ((n % m) + m) % m
    }
  }
}
</script>

<style lang="scss" scoped>

.vertical-gallery {
  overflow-x: hidden;
}

.horizontal-gallery {
  position: relative;
  // overflow:hidden;
}

::v-deep .gallery-element {
  position: absolute;
  height: 100%;

  @include breakpoint(md-and-up) {
    &[data-leavex='-100%'] {
      .gallery-element-container::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        margin-left: 60px;
        background: rgba(255, 255, 255, 0.01);
        z-index: 9;
      }
    }

    &[data-leavex='100%'] {
      .gallery-element-container::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        margin-left: -60px;
        background: rgba(255, 255, 255, 0.01);
        z-index: 9;
      }
    }
  }
}

.photo-desc-spacer {
  width: 50px;
  height: 1px;
  background-color: map-get($shades, 'black');
}

.gallery-items-move {
  transition: transform 0.5s $ease-in-out-quad;
}

.gallery-items-enter,
.gallery-items-leave-to {
  // transform: translate3d(attr(data-leavex), 0, 0);
  transform: translate3d(0, 0, 0);
  transform: translate3d(var(--leavex, '0'), 0, 0);
  opacity: 0;
}

.gallery-items-enter-active,
.gallery-items-leave-active {
  transition: transform 0.5s $ease-in-out-quad, opacity 0.25s $ease-in-out-quad;
}

.photo-info {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  transform: translateY(100%);
}

.artist-info {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transform: translateY(-100%);
}

</style>
